import { Injectable } from '@angular/core';
import { SwPush } from '@angular/service-worker';
import { DataService } from 'src/app/shared/data.service';
import { Observable, Subject, from, of } from 'rxjs';
import { take, switchMap, tap, catchError } from 'rxjs/operators';


@Injectable({ providedIn: 'root' })
export class NotificationService {

  readonly VAPID_PUBLIC_KEY = 'BBe6gITx6rMzg-qSIuPxmz5oaN6SdFvQdvA-0RdYGzbTMjIGIWbDhKQUJSgmx18YYxXNgICo-FZAPKQabvaw0Z0';

  private notificationSubject = new Subject<any[]>();
  notification$ = this.notificationSubject.asObservable();
  notifications: any[] = [];
  badgeCount = { value: 0 };

  constructor(
    private swPush: SwPush,
    private dataService: DataService
  ) {}

  /** Initialise l'écoute des messages push */
  initializeMessageListener(): void {
    if (!this.swPush.isEnabled) return;

    this.swPush.messages.subscribe({
      next: (message: any) => {
        const notif = message.notification;
        notif.type = this.getNotificationType(notif);
        notif.created_at = new Date();
        this.badgeCount.value++;
        this.notifications.unshift(notif);
        this.notificationSubject.next([...this.notifications]);
      },
      error: (err) => console.error('[Push] Error:', err)
    });
  }

  /** S'abonner aux notifications push */
  subscribeToNotifications(alerter = false): Observable<PushSubscription | null> {
    if (!this.swPush.isEnabled) {
      if (alerter) alert('Les notifications push ne sont pas disponibles');
      return of(null);
    }

    return this.swPush.subscription.pipe(
      take(1),
      switchMap(existing => {
        if (existing) {
          this.syncSubscriptionWithBackend(existing);
          if (alerter) alert('Vous êtes déjà enregistré aux notifications');
          return of(existing);
        }
        return from(this.swPush.requestSubscription({ serverPublicKey: this.VAPID_PUBLIC_KEY })).pipe(
          tap(sub => {
            this.syncSubscriptionWithBackend(sub);
            if (alerter) alert('Notifications activées avec succès');
          }),
          catchError(err => {
            if (alerter) alert('Impossible d\'activer les notifications');
            throw err;
          })
        );
      })
    );
  }

  private syncSubscriptionWithBackend(sub: PushSubscription): void {
    const data = { ...sub.toJSON(), device_name: this.generateDeviceName() };
    this.dataService.savePushSubscription(data).subscribe();
  }

  private generateDeviceName(): string {
    const ua = navigator.userAgent;
    let browser = 'Unknown', os = 'Unknown';

    if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
    else if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
    else if (ua.includes('Edg')) browser = 'Edge';

    if (ua.includes('Windows')) os = 'Windows';
    else if (ua.includes('Mac')) os = 'macOS';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (/iPhone|iPad/.test(ua)) os = 'iOS';

    const date = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    return `${browser} - ${os} (${date})`;
  }

  private getNotificationType(data: any): string {
    const text = `${data.title || ''} ${data.body || ''}`.toLowerCase();
    if (text.includes('signature') || text.includes('signé')) return 'signature';
    if (text.includes('rdv') || text.includes('rendez-vous')) return 'rdv';
    if (text.includes('paiement')) return 'paiement';
    if (text.includes('réclamation') || text.includes('reclamation')) return 'reclamation';
    if (text.includes('dossier') || text.includes('complet')) return 'dossier_complete';
    return 'devis';
  }

  updateNotifications(notifications: any[]): void {
    this.notifications = notifications;
    this.notificationSubject.next([...this.notifications]);
  }
}
